import { get, isNil, keyBy } from "lodash";
import { EDGE_KINDS, NODE_TYPES } from "utils/constants";
import { operationSymbol } from "./catalog";

const STATUS_FIELDS = [
  "name",
  "tag",
  "color",
  "background",
  "progress",
  "order",
  "template",
  "moveToNext",
];

const hasPosition = (position) =>
  !isNil(position) && !isNil(get(position, "x")) && !isNil(get(position, "y"));

const pickStatusData = (status) =>
  STATUS_FIELDS.reduce((data, field) => {
    data[field] = get(status, field);
    return data;
  }, {});

export const conditionSummary = (conditions = []) =>
  conditions
    .filter((condition) => !isNil(condition))
    .map((condition) =>
      [
        get(condition, "leftOperand.accessor"),
        operationSymbol(get(condition, "operation")),
        get(condition, "rightOperand.value"),
      ]
        .filter((part) => !isNil(part) && part !== "")
        .join(" "),
    )
    .filter((part) => part !== "")
    .join(" · ");

export const edgeSummary = (possibleStatus = {}) => {
  const kind = possibleStatus.kind || EDGE_KINDS.CONDITION;
  if (kind === EDGE_KINDS.EVENT)
    return possibleStatus.event ? `⚡ ${possibleStatus.event}` : "⚡";
  if (kind === EDGE_KINDS.TIMER)
    return isNil(possibleStatus.delaySeconds)
      ? "⏱"
      : `⏱ ${possibleStatus.delaySeconds}s`;
  if (kind === EDGE_KINDS.ALWAYS) return "→";
  return conditionSummary(possibleStatus.conditions);
};

const latestVersion = (versions = []) =>
  versions.reduce(
    (latest, version) =>
      isNil(latest) ||
      Number(get(version, "versionNumber")) >
        Number(get(latest, "versionNumber"))
        ? version
        : latest,
    null,
  );

// Admin canvas prefers the latest version so draft HTML edits show before
// publish. Runtime (CanvasRunner) still reads currentPublishedVersion only.
const previewVersion = (screen) =>
  latestVersion(screen.versions) || screen.currentPublishedVersion;

const previewScreen = (status) => {
  const screens = status.screens || [];
  const screenWithHtml = screens.find((screen) => {
    const version = previewVersion(screen);
    return (
      !isNil(get(version, "htmlContent")) && get(version, "htmlContent") !== ""
    );
  });
  return screenWithHtml || screens[0] || null;
};

export const toGraph = (processStatuses = []) => {
  const nodes = processStatuses.map((status) => {
    const screen = previewScreen(status);
    const version = screen ? previewVersion(screen) : null;
    return {
      id: status.id,
      type: "status",
      position: hasPosition(status.position) ? status.position : { x: 0, y: 0 },
      data: {
        ...pickStatusData(status),
        nodeType: status.nodeType || NODE_TYPES.STATUS,
        config: status.config || {},
        formDefinition: status.formDefinition || null,
        entryConnectors: status.entryConnectors || [],
        outputConnectors: status.outputConnectors || [],
        dataBindings: status.dataBindings || [],
        screenCount: (status.screens || []).length,
        screenId: screen ? screen.id : null,
        screenVersionId: get(version, "id") || null,
        screenVersionNumber: get(version, "versionNumber") || null,
        screenVersionStatus: get(version, "status") || null,
        screenHtml: get(version, "htmlContent") || null,
        screenStyleContent: get(version, "styleContent") || null,
        screenSlots: Array.isArray(get(version, "bindingManifest.slots"))
          ? get(version, "bindingManifest.slots")
          : [],
        screenManifestId: get(version, "bindingManifest.id") || null,
        screenReport: get(version, "bindingManifest.report") || null,
        screenStates: get(version, "states") || [],
      },
    };
  });

  const edges = processStatuses.flatMap((status) =>
    (status.possibleStatuses || [])
      .filter((possibleStatus) => !isNil(possibleStatus.nextStatus))
      .map((possibleStatus) => ({
        id: possibleStatus.id || `${status.id}->${possibleStatus.nextStatus}`,
        source: status.id,
        target: possibleStatus.nextStatus,
        label: edgeSummary(possibleStatus),
        data: {
          possibleStatusId: possibleStatus.id,
          order: possibleStatus.order,
          kind: possibleStatus.kind || EDGE_KINDS.CONDITION,
          event: possibleStatus.event || "",
          delaySeconds: isNil(possibleStatus.delaySeconds)
            ? null
            : possibleStatus.delaySeconds,
          conditions: possibleStatus.conditions || [],
        },
      })),
  );

  return { nodes, edges };
};

export const fromGraph = (nodes = [], edges = [], baseStatuses = []) => {
  const baseById = keyBy(baseStatuses, "id");
  const edgesBySource = edges.reduce((map, edge) => {
    (map[edge.source] = map[edge.source] || []).push(edge);
    return map;
  }, {});

  return nodes.map((node) => {
    const base = baseById[node.id] || {};
    return {
      id: node.id,
      ...pickStatusData(node.data),
      nodeType: get(node, "data.nodeType") || NODE_TYPES.STATUS,
      config: get(node, "data.config") || {},
      formDefinition: get(node, "data.formDefinition") || null,
      entryConnectors: get(node, "data.entryConnectors") || [],
      outputConnectors: get(node, "data.outputConnectors") || [],
      dataBindings: get(node, "data.dataBindings") || [],
      position: hasPosition(node.position) ? node.position : { x: 0, y: 0 },
      possibleStatuses: (edgesBySource[node.id] || []).map((edge) => ({
        id: get(edge, "data.possibleStatusId") || edge.id,
        nextStatus: edge.target,
        order: get(edge, "data.order"),
        kind: get(edge, "data.kind") || EDGE_KINDS.CONDITION,
        event: get(edge, "data.event") || null,
        delaySeconds: isNil(get(edge, "data.delaySeconds"))
          ? null
          : Number(get(edge, "data.delaySeconds")),
        conditions: get(edge, "data.conditions") || [],
      })),
      actions: base.actions || [],
    };
  });
};
